import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGun
} from '@fortawesome/free-solid-svg-icons';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import Cta from '@/components/cta';
import { FAQ } from '@/components/faq';
import { Partners } from '@/components/partners';
import { useTranslation } from "react-i18next";
import { t } from 'i18next';
import AdvantagesSlider from './AdvantagesSlider';
import { Button } from '@/components/ui/button';
import { Link } from 'wouter';
import { HowItWorks } from '@/components/HowItWorks';
import { ICONS } from '@/assets/icons';

type Feature = {
  icon: any;
  title: string;
  desc: string;
};


const advantages: Feature[] = [
  {
    icon: ICONS.preventionBeforeEscalation,
    title: t("models.violenceDetection.details.advantages.title1"),
    desc: t("models.violenceDetection.details.advantages.description1")
  },
  {
    icon: ICONS.realTimeAction,
    title: t("models.violenceDetection.details.advantages.title2"),
    desc: t("models.violenceDetection.details.advantages.description2")
  },
  {
    icon: ICONS.increasedAccuracy,
    title: t("models.violenceDetection.details.advantages.title3"),
    desc: t("models.violenceDetection.details.advantages.description3")
  },
  {
    icon: ICONS.minimalFalseAlarm,
    title: t("models.violenceDetection.details.advantages.title4"),
    desc: t("models.violenceDetection.details.advantages.description4")
  },
  {
    icon: ICONS.enhanceSafety,
    title: t("models.violenceDetection.details.advantages.title5"),
    desc: t("models.violenceDetection.details.advantages.description5")
  },
  {
    icon: ICONS.fastResponseTime,
    title: t("models.violenceDetection.details.advantages.title6"),
    desc: t("models.violenceDetection.details.advantages.description6")
  },
  {
    icon: ICONS.comprehensiveThreatDetection,
    title: t("models.violenceDetection.details.advantages.title7"),
    desc: t("models.violenceDetection.details.advantages.description7")
  },
];


const howItWorks = [
  {
    title: t('models.violenceDetection.details.howItWorks.title1'),
    desc: t('models.violenceDetection.details.howItWorks.description1'),
    icon: ICONS.analyseTempsReel
  },
  {
    title: t('models.violenceDetection.details.howItWorks.title2'),
    desc: t('models.violenceDetection.details.howItWorks.description2'),
    icon: ICONS.movementRecognition
  },
  {
    title: t('models.violenceDetection.details.howItWorks.title3'),
    desc: t('models.violenceDetection.details.howItWorks.description3'),
    icon: ICONS.knifeDetection
  },
  {
    title: t('models.violenceDetection.details.howItWorks.title4'),
    desc: t('models.violenceDetection.details.howItWorks.description4'),
    icon: ICONS.alertsInstantanees
  }
];

const knife = [
  {
    title: t('models.violenceDetection.details.detections.knife.point1'),
    desc: t('models.violenceDetection.details.detections.knife.description1'),
    icon: ICONS.knifeDetection // Closest to knife in FontAwesome
  },
  {
    title: t('models.violenceDetection.details.detections.knife.point2'),
    desc: t('models.violenceDetection.details.detections.knife.description2'),
    icon: ICONS.knifeDetection
  },
  {
    title: t('models.violenceDetection.details.detections.knife.point3'),
    desc: t('models.violenceDetection.details.detections.knife.description3'),
    icon: ICONS.knifeDetection
  },

]

const guns = [
  {
    title: t('models.violenceDetection.details.detections.gun.point1'),
    desc: t('models.violenceDetection.details.detections.gun.description1'),
    icon: faGun // Use faGun if available, else faCrosshairs
  },
  {
    title: t('models.violenceDetection.details.detections.gun.point2'),
    desc: t('models.violenceDetection.details.detections.gun.description2'),
    icon: faGun
  },
  {
    title: t('models.violenceDetection.details.detections.gun.point3'),
    desc: t('models.violenceDetection.details.detections.gun.description3'),
    icon: faGun
  },

]

const fight = [
  {
    title: t('models.violenceDetection.details.detections.fight.point1'),
    desc: t('models.violenceDetection.details.detections.fight.description1'),
    icon: ICONS.fightDetection // Use faHandFist or faUserNinja for fight
  },
  {
    title: t('models.violenceDetection.details.detections.fight.point2'),
    desc: t('models.violenceDetection.details.detections.fight.description2'),
    icon: ICONS.fightDetection
  },
  {
    title: t('models.violenceDetection.details.detections.fight.point3'),
    desc: t('models.violenceDetection.details.detections.fight.description3'),
    icon: ICONS.fightDetection
  },

]


const ViolenceModelDetails = () => {
  const { t } = useTranslation();

  const [imgInView, setImgInView] = useState(false);
  const [stepInView, setStepInView] = useState([false, false, false]);

  useEffect(() => {
    setImgInView(true);
    const timers = howItWorks.map((_, i) =>
      setTimeout(() => setStepInView((prev) => {
        const newState = [...prev];
        newState[i] = true;
        return newState;
      }), i * 300)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <>
      <Header />
      {/* Hero Section */}
      <div id="hero" className="relative h-screen w-full overflow-hidden">
        {/* Background GIF with Overlay */}
        <div className="absolute inset-0 z-0">
          {/* Blue gradient overlays for cinematic darkening */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#001a33]/80 via-[#001a33]/60 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.35)_0%,_rgba(0,0,0,0.65)_100%)]" />
          <img
            src="./gun.png"
            alt="Fall detection visual"
            className={`w-full h-auto object-cover rounded-xl shadow-2xl transition-all duration-700 ease-in-out ${imgInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto sm:mx-0 text-white px-6 sm:pl-6 sm:pr-0 md:pl-6 lg:pl-6 xl:pl-6 text-center sm:text-left">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-korolev font-bold leading-tight tracking-wide">
                <span className="block pt-3 sm:pt-4 md:pt-5 text-primary-200/90 tracking-wider">
                  {t('models.violenceDetection.details.title')}
                </span>
              </h1>
              <h2 className="hidden sm:block mt-3 sm:mt-5 text-lg sm:text-2xl md:text-2xl font-korolev font-medium text-gray-100 max-w-2xl">
                {t('models.violenceDetection.details.description')}
              </h2>


              <div className="mt-6 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4 font-poppins">
                <Link href="/free-trial" className="group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-black hover:bg-gray-50 hover:scale-[1.02] px-4 py-4 sm:px-6 sm:py-6 text-sm sm:text-lg font-medium transition-all duration-300 hover:shadow-lg group-hover:shadow-primary/20"
                  >
                    <span className="relative z-10">
                      {t('models.requestDemo')}
                    </span>
                  </Button>
                </Link>
                <Link
                  href="/contact-sales"
                  className="group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto bg-transparent border-white/70 text-white hover:bg-white/10 hover:border-white/90 hover:scale-[1.02] px-4 py-4 sm:px-6 sm:py-6 text-sm sm:text-lg font-medium transition-all duration-300 hover:shadow-lg group-hover:shadow-white/10"
                  >
                    <span className="relative z-10">{t('models.contactUs')}</span>
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </div>
        {/* Scroll Indicator */}
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 opacity-40">
          <button
            onClick={() => {
              window.scrollTo({
                top: window.innerHeight,
                behavior: 'smooth'
              });
            }}
            aria-label="Scroll down"
            className="flex flex-col items-center text-white hover:text-primary-200 transition-colors group"
          >
            <span className="text-sm font-medium mb-2 opacity-80 group-hover:opacity-100 transition-opacity">
              {t('hero.scrollDown', 'Scroll down')}
            </span>
            <div className="w-10 h-16 rounded-full border-2 border-white/50 flex items-start justify-center p-1 group-hover:border-white transition-colors">
              <div className="w-1 h-4 bg-white rounded-full animate-bounce" />
            </div>
          </button>
        </div>
      </div>
      {/* How It Works Section */}
      <HowItWorks
        title={t('models.violenceDetection.details.howItWorks.title')}
        steps={howItWorks}
        stepInView={stepInView}
      />

      {/* What Can Oleyes Detect Section */}
      <section className="py-12 sm:py-20 bg-slate-900 dark:bg-black text-white px-4 md:px-8 lg:px-16 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-16 text-center tracking-tight bg-clip-text bg-gradient-to-r from-white to-slate-400 dark:from-white dark:to-slate-400">
            {t('models.violenceDetection.details.detections.title', 'What Can Oleyes Detect?')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Knife Detection */}
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-primary-500/50 transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-2xl">
                  <img className="w-10 h-10" src={knife[0].icon} alt="Knife Detection" />
                </div>
                <h3 className="text-xl font-bold">
                  {t('models.violenceDetection.details.detections.knife.title', 'Knife Detection')}
                </h3>
              </div>
              <ul className="space-y-4">
                {knife.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-slate-700 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-primary-400 text-xs font-bold">{idx + 1}</span>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      <span className="font-medium text-slate-100">{item.title}:</span> {item.desc}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Gun Detection */}
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-primary-500/50 transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-2xl">
                  <FontAwesomeIcon icon={guns[0].icon} />
                </div>
                <h3 className="text-xl font-bold">
                  {t('models.violenceDetection.details.detections.gun.title', 'Gun Detection')}
                </h3>
              </div>
              <ul className="space-y-4">
                {guns.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-slate-700 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-primary-400 text-xs font-bold">{idx + 1}</span>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      <span className="font-medium text-slate-100">{item.title}:</span> {item.desc}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Fight Detection */}
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-primary-500/50 transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-2xl">
                  <img className="w-10 h-10" src={fight[0].icon} alt="Fight Detection" />
                </div>
                <h3 className="text-xl font-bold">
                  {t('models.violenceDetection.details.detections.fight.title', 'Fight Detection')}
                </h3>
              </div>
              <ul className="space-y-4">
                {fight.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-slate-700 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-primary-400 text-xs font-bold">{idx + 1}</span>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      <span className="font-medium text-slate-100">{item.title}:</span> {item.desc}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages and Limitations Section */}
      <section className=" sm:mt-24 py-12 sm:py-20 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 text-slate-900 dark:text-white px-2 sm:px-4 md:px-8 lg:px-16 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center tracking-tight bg-clip-text  bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-300">
            {t("models.violenceDetection.details.advantages.title")}
          </h2>
          <div className="flex flex-col mt-8 sm:mt-16 lg:flex-row gap-10 lg:gap-16 items-stretch w-full">
            {/* Advantages Slider */}
            <div className="flex-1 flex flex-col min-w-0 max-w-full h-full">

              <div className="flex-1 flex flex-col h-full min-h-[260px]">
                <AdvantagesSlider items={advantages}
                  itemClassName="group flex items-center gap-4 py-4 px-6 border-b border-slate-200 dark:border-slate-700 last:border-0 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-300 min-h-[90px]"
                />
              </div>
            </div>

          </div>
        </div>
      </section>



      <Partners />
      <Cta />
      <div className="mt-20 mb-20 mx-10 md:mx-0">
        <FAQ faqs={t('models.violenceDetection.details.faq.faq', { returnObjects: true }) as Array<{ id?: string, question: string, answer: string }>} title={t('faq.title', 'Frequently Asked Questions')} subtitle={t('benefitsCloud.details.scalability.faqSubtitle', 'Everything you need to know about scalability')} />
      </div>
      <Footer />
    </>
  );
};

export default ViolenceModelDetails;
